import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MinuteStyle } from 'components/minute/layout/CreateMinuteStyle/MinuteStyle';
import { S_WebButtonSmaller } from 'elements/button/WebButton';
import { recalculateCoordinatesForNewImage } from 'hooks/coordinates/useCoordinatesComputations';
import { useUploadImage } from 'libs/hooks/io/useUploadImage';
import { EColors, EFonts, EShadows } from 'libs/styles/styling';
import { BACKGROUND_IMAGE_DEFAULT_COORDINATES } from 'libs/utils/map/mapImageBackground';
import { getCoordinateBounds } from 'libs/utils/world/worldUtils';
import { useEffect, useState } from 'react';
import { ICoordinates } from 'react-give-me-map';
import { MapRef } from 'react-map-gl';
import { BLANK_STYLE, useStyles } from 'static/config/stylesConfig';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

export type ChooseStyleData =
    | {
          type: 'basic';
          url: string;
      }
    | { type: 'image'; imageUrl: string; coordinates: ICoordinates[]; url: string };

interface Props {
    onStyleSelected: (style: ChooseStyleData) => void;
    mapRef: React.RefObject<MapRef>;
    onCreateClicked: () => void;
    className?: string;
}

export const ChooseMinuteStyle = (props: Props): JSX.Element => {
    const styles = useStyles();
    const [selectedStyle, setSelectedStyle] = useState<string>(styles.BASIC_STYLES[0].url);
    const [chosen, setChosen] = useState<boolean>(true);
    const [customColors, setCustomColors] = useState<boolean>(false);
    const { uploaded, setFiles, inProgress } = useUploadImage([4096], 'TODO'); //create before?
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        if (uploaded?.data?.imageUrls && props.mapRef?.current) {
            const imageUrl = uploaded?.data?.imageUrls[4096];

            const bounds = getCoordinateBounds(BACKGROUND_IMAGE_DEFAULT_COORDINATES);

            props.mapRef.current?.fitBounds(bounds, {
                padding: {
                    top: 50,
                    left: 50,
                    bottom: 50,
                    right: 50
                },
                animate: false
            });

            props.mapRef?.current.loadImage(imageUrl, (error, image) => {
                const newCoordinates = recalculateCoordinatesForNewImage(
                    props.mapRef?.current,
                    BACKGROUND_IMAGE_DEFAULT_COORDINATES,
                    image
                );

                props.onStyleSelected({
                    type: 'image',
                    imageUrl,
                    coordinates: newCoordinates,
                    url: BLANK_STYLE
                });
            });
        }
    }, [uploaded, props.mapRef]);

    return (
        <S_ChooseMinuteStyle className={props.className}>
            {!customColors && (
                <>
                    <S_Title>{Localization.CHOOSE_YOUR_STYLE}</S_Title>
                    <S_Buttons>
                        {styles.BASIC_STYLES.map((style) => {
                            return (
                                <S_MinuteStyle
                                    key={style.name}
                                    name={style.name}
                                    img={style.img}
                                    selected={selectedStyle === style.url}
                                    onSelected={() => {
                                        setSelectedStyle(style.url);
                                        setChosen(true);
                                        props.onStyleSelected({ type: 'basic', url: style.url });
                                    }}
                                />
                            );
                        })}
                        <S_MinuteStyle
                            name={Localization.COLORED}
                            img={'https://dvtqxks0zalev.cloudfront.net/app-static/style6.jpg'}
                            selected={styles.COLOR_STYLES.some((e) => e.url === selectedStyle)}
                            onSelected={() => {
                                setCustomColors(true);
                            }}
                        />
                        <label>
                            <S_InputFile
                                type="file"
                                accept="image/x-png,image/png,image/gif,image/jpeg,image/jpg,image/heic,image/heif,image/svg+xml,.heic;capture=camera"
                                onChange={(e) => {
                                    setFiles(Array.from(e.target['files']));
                                }}
                            />
                            <S_MinuteStyle
                                name={inProgress ? Localization.UPLOADING : Localization.IMAGE_MAP}
                                icon={inProgress ? 'spinner' : 'upload'}
                                selected={selectedStyle === BLANK_STYLE}
                                onSelected={() => {
                                    if (!inProgress) {
                                        setSelectedStyle(BLANK_STYLE);
                                        setChosen(true);
                                    }
                                }}
                            />
                        </label>
                    </S_Buttons>
                </>
            )}
            {customColors && (
                <>
                    <S_BackClick
                        onClick={() => {
                            setCustomColors(false);
                        }}
                    >
                        <S_Back icon={['fal', 'arrow-left']} />
                        <span>{Localization.BACK}</span>
                    </S_BackClick>
                    <S_Buttons style={{ margin: '0 0 4px 0' }}>
                        {styles.COLOR_STYLES.map((style) => {
                            return (
                                <S_OtherMinuteStyle
                                    key={style.url}
                                    color={style.color}
                                    selected={selectedStyle === style.url}
                                    small={true}
                                    onSelected={() => {
                                        setSelectedStyle(style.url);
                                        setChosen(true);
                                        props.onStyleSelected({ type: 'basic', url: style.url });
                                    }}
                                />
                            );
                        })}
                    </S_Buttons>
                </>
            )}
            <S_Center>
                <S_WebButton
                    shadow={EShadows.LIGHT}
                    disabled={!chosen || inProgress > 0}
                    type={'primary'}
                    text={Localization.I_LIKE_THIS_ONE}
                    onClick={() => {
                        setProcessing(true);
                        props.onCreateClicked();
                    }}
                    loading={processing}
                />
            </S_Center>
        </S_ChooseMinuteStyle>
    );
};

const S_ChooseMinuteStyle = styled.div`
    width: 100%;
    border-radius: 7px;
    background-color: ${EColors.GREY2};
    box-shadow: ${EShadows.STRONG};
    padding: 20px 30px;
    display: flex;
    flex-direction: column;
    @media only screen and (min-width: 1260px) {
        width: 535px;
    }
`;

const S_Title = styled.div`
    color: ${EColors.DARK};
    font-size: 16px;
    font-family: ${EFonts.Medium};
    margin: 0 0 15px 0;
    display: flex;
    align-items: center;
`;

const S_Buttons = styled.div`
    display: flex;
    flex-wrap: wrap;
    margin: 5px 0 0 0;
`;

const S_MinuteStyle = styled(MinuteStyle)`
    margin: 0 10px 10px 10px;
    @media only screen and (min-width: 1260px) {
        &:nth-child(1),
        &:nth-child(5) {
            margin: 0 10px 10px 0;
        }
    }
`;

const S_Center = styled.div`
    display: flex;
    justify-content: center;
`;

const S_WebButton = styled(S_WebButtonSmaller)`
    margin: 20px 0 15px 0;
`;

const S_Back = styled(FontAwesomeIcon)`
    font-size: 22px;
    margin: 0 15px 0 0;
`;

const S_BackClick = styled(S_Title)`
    cursor: pointer;
    margin: 0 0 20px 0;
`;

const S_OtherMinuteStyle = styled(MinuteStyle)`
    margin: 0 5px 10px 5px;
    @media only screen and (min-width: 1260px) {
        &:nth-child(1),
        &:nth-child(8),
        &:nth-child(15),
        &:nth-child(22) {
            margin: 0 5px 10px 0;
        }
        &:nth-child(7),
        &:nth-child(14),
        &:nth-child(21),
        &:nth-child(28) {
            margin: 0 0px 10px 5px;
        }
    }
`;

const S_InputFile = styled.input`
    display: none;
`;
